import React from 'react';
import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import {
  getVerificationStatusLabel,
  ORDER_STATUS,
} from '../../../utils/settings';
import StatusBadge from '../../../v2/MUI_custom_component/Badge/StatusBadge';
import { CustomButton } from '../../../v2/MUI_custom_component';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  initDP,
  resetDP,
  resetDpRouteSource,
  resetSelectedShipToParty,
  setMoQData,
} from '../../DP/CreateDP/reducer/DpActions';

const PurchaseOrdersSection = ({
  title = 'Purchase Orders',
  showViewAll = true,
  poList = [],
  onEditPO,
  MoQData,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleViewAll = () => {
    navigate('/po-list');
  };

  const handleCreateDP = (po) => {
    dispatch(resetDP());
    dispatch(resetDpRouteSource());
    dispatch(resetSelectedShipToParty());
    dispatch(initDP(po.humbee_po_number, po?.po_remaining_qty));
    dispatch(setMoQData(MoQData));
    navigate('/create-dp', {
      state: {
        humbee_po_number: po.humbee_po_number,
        po_remaining_qty: po?.po_remaining_qty || null,
      },
    });
  };

  const getStatusColor = (status) => {
    const statusColorMap = {
      pending: '#FF9800',
      approved: '#4CAF50',
      partially_completed: '#2196F3',
      completed: '#4CAF50',
      cancelled: '#F44336',
    };
    return statusColorMap[status] || '#757575';
  };

  if (poList.length === 0) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1}}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant='h2'>{title}</Typography>
        {showViewAll && (
          <CustomButton
            label='View all'
            variant='text'
            color='primary'
            onClick={handleViewAll}
            size='small'
          />
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          overflowX: { xs: 'scroll' },
          gap: 1,
          pb: 1,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {poList.map((po, index) => (
          <Box
            key={po.humbee_po_number || index}
            sx={{
              p: 1,
              borderRadius: 2,
              backgroundColor: '#fff',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              width: '164px',
              minWidth: '164px',
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography variant='bodyL'>
                  {dayjs(po.booking_date).format('DD MMM YYYY')}
                </Typography>
                <Typography variant='bodyL'>
                  {po.user_po_number || `{User PO No.}`}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginTop:0.5 }}>
                  <StatusBadge
                    label={getVerificationStatusLabel(po.status)}
                    status={getStatusColor(po.status)}
                    size='small'
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: {xs: 0.5, md: 1},width: '100%', paddingBottom: 1}}
              >
                {po.quantity_info &&
                  po.quantity_info.length > 0 &&
                  po.quantity_info.map((item, index) => (
                    <Box
                      key={index}
                      sx={{ display: 'flex', gap:1}}
                    >
                      <Typography variant='labelM' sx={{ color: '#666' , minwidth: 64, width: 80, flexShrink: 0}}>
                        {item.label}.
                      </Typography>
                      <Typography variant='bodyM' sx={{ whiteSpace: 'nowrap'}}>
                        {item.value} {item.unit}
                      </Typography>
                    </Box>
                  ))}
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, width: '100%', mt: 'auto' }}>
              {(po.status === ORDER_STATUS.APPROVED ||
                po.status === ORDER_STATUS.PARTIALLY_COMPLETED) && (
                <CustomButton
                  label='Create DP'
                  variant='outlined'
                  color='primary'
                  size='small'
                  fullWidth
                  onClick={() => handleCreateDP(po)}
                />
              )}
              {po.status === ORDER_STATUS.PENDING && onEditPO && (
                <CustomButton
                  label='Edit PO'
                  variant='outlined'
                  color='primary'
                  size='small'
                  fullWidth
                  onClick={() => onEditPO(po)}
                />
              )}
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PurchaseOrdersSection;
